import { forwardRef, useState } from 'react';

import { DealerResultBeforeSearch } from '@/components/sections/dealer/result/before-search';
import { DealerResultDataList } from '@/components/sections/dealer/result/data-list';
import { DealerResultMap } from '@/components/sections/dealer/result/map';
import { DealerResultMobileSelectTab } from '@/components/sections/dealer/result/mobile-select-tab';
import { DealerResultNotFound } from '@/components/sections/dealer/result/not-found';
import { LoadingSpinner } from '@/components/shared/element/loading-spinner';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Section } from '@/components/shared/layout/section';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { IDealer } from '@/types/dealer/dealer';
import { ILocation } from '@/types/dealer/location';
import { IVehicleType } from '@/types/services/contentstack';
import { IIcon } from '@/types/shared';

enum MobileDealerSelectTab {
  List,
  Map,
}

interface IMobileDealerSelectTab {
  key: MobileDealerSelectTab;
  icon: IIcon;
  label: string;
}

interface DealerResultProps {
  showMapResult: boolean;
  currentLocation?: ILocation;
  vehicleType: IVehicleType;
  dealers: IDealer[];
  selectedDealer?: IDealer;
  beforeSearchText: string;
  beforeSearchImage?: ImageProps;
  isLoadingDealer: boolean;
  onSelectDealer: (id: string) => void;
  onSelectDealerFromMarker: (id: string) => void;
}

const DEFAULT_MAP_CENTER = {
  lat: 13.7625534,
  lng: 100.5724117,
};

const MOBILE_SELECT_RESULT_TABS: IMobileDealerSelectTab[] = [
  {
    key: MobileDealerSelectTab.List,
    icon: 'list',
    label: 'รายชื่อ',
  },
  {
    key: MobileDealerSelectTab.Map,
    icon: 'place',
    label: 'ตำแหน่ง',
  },
];

export const DealerResult = forwardRef(
  (props: DealerResultProps, forwardRef: any) => {
    const [tab, setTab] = useState<MobileDealerSelectTab>(
      MobileDealerSelectTab.List
    );
    return (
      <>
        {!props.showMapResult && (
          <DealerResultBeforeSearch
            message={props.beforeSearchText}
            imageDesktop={props.beforeSearchImage}
          />
        )}
        <Section
          ref={forwardRef}
          test="section_dealer_list"
          as="div"
          css={{
            display: props.showMapResult ? 'block' : 'none',
            height: 600,
            marginBottom: '$space-9',
            '@lg': {
              marginBottom: '$space-11',
              bc: '$gray200',
            },
          }}
        >
          {!!props.showMapResult &&
            (props?.dealers?.length === 0 ? (
              <Box position="absolute" width="full" height="full">
                <DealerResultNotFound data-test="dealer_not_found" />
              </Box>
            ) : (
              <Flex
                direction="column"
                css={{
                  height: '100%',
                  bc: '$white',
                }}
              >
                <DealerResultMobileSelectTab
                  tabs={MOBILE_SELECT_RESULT_TABS}
                  tab={tab}
                  onSelectTab={setTab}
                />
                <Flex
                  columnGap={2}
                  css={{
                    overflow: 'hidden',
                    height: '100%',
                    '@maxlg': {
                      columnGap: 0,
                    },
                  }}
                >
                  <Box
                    css={{
                      flexBasis: '100%',
                      maxWidth: '100%',
                      display:
                        tab === MobileDealerSelectTab.List ? 'block' : 'none',
                      '@lg': {
                        display: 'block !important',
                        flexBasis: '512px',
                        maxWidth: '512px',
                      },
                    }}
                  >
                    <Flex
                      direction="column"
                      css={{
                        height: '100%',
                        borderBottom: '1px solid $gray300',
                      }}
                    >
                      {!!props.dealers && (
                        <DealerResultDataList
                          dealers={props.dealers}
                          selectedDealer={props.selectedDealer}
                          currentLocation={props.currentLocation}
                          vehicleType={props.vehicleType}
                          onOpenDrawer={props.onSelectDealer}
                        />
                      )}
                    </Flex>
                  </Box>
                  <Box
                    position="relative"
                    css={{
                      bc: '$gray900',
                      flexBasis: '100%',
                      maxWidth: '100%',
                      display:
                        tab === MobileDealerSelectTab.Map ? 'block' : 'none',
                      minHeight: 400,
                      '@lg': {
                        display: 'block !important',
                        flexGrow: '1',
                        flexBasis: '0',
                      },
                    }}
                  >
                    <DealerResultMap
                      center={DEFAULT_MAP_CENTER}
                      selectCenter={DEFAULT_MAP_CENTER}
                      dealers={props.dealers}
                      selectedDealer={props.selectedDealer}
                      onClickMarker={props.onSelectDealerFromMarker}
                    />
                  </Box>
                </Flex>
              </Flex>
            ))}
          {!!props.isLoadingDealer && (
            <LoadingSpinner
              css={{
                zIndex: 2,
                bc: '$white',
                '@lg': {
                  bc: '$gray300',
                },
              }}
            />
          )}
        </Section>
      </>
    );
  }
);

DealerResult.displayName = 'SectionDealerResult';
